import type { MultilingualLabel } from '@sparted/shared-library/business/types';
import { t } from 'i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { COLORS, TYPOGRAPHY } from 'Components/foundation';
import MModal from 'Components/modal/MModal/MModal';
import SInput from 'Components/structural/SInput/SInput';
import STextArea from 'Components/structural/STextArea/STextArea';
import UFlag from 'Components/unit/UFlag/UFlag';
import UIcon from 'Components/unit/UIcon/UIcon';
import ULabel from 'Components/unit/ULabel/ULabel';
import ULanguageMenu, { type OptionType } from 'Components/unit/ULanguageMenu/ULanguageMenu';
import { enqueueBasicAlert } from 'Pages/AlertManager/redux/utils';
import { Language } from 'Services/language/languageService';
import { fetchTranslations } from 'Services/translator/translatorService';

import SCallout, { type SCalloutProps } from '../SCallout/SCallout';
import styles from './STranslationInterface.style';

const ALERT_DURATION = 5000;

type TranslateFieldProps = Omit<TranslationField, 'name'> & {
  value: string;
  setValue: (value: string) => void;
  originalValue: string;
};
const TranslateField = ({ label, value, originalValue, type, maxCharacter, setValue }: TranslateFieldProps) => {
  return (
    <div style={styles.inputContainer}>
      <ULabel size="M">{label}</ULabel>
      <div style={{ ...styles.inputs, marginTop: maxCharacter ? '0' : '24px' }}>
        {type === 'textarea' ? (
          <STextArea
            value={originalValue}
            onChange={() => {}}
            disabled
            style={{ marginTop: maxCharacter ? '18px' : '0' }}
          />
        ) : (
          <SInput
            value={originalValue}
            onChange={() => {}}
            type="large"
            disabled
            extraInputStyle={{ color: COLORS.TEXT.DEFAULT }}
            style={{ opacity: 1, marginTop: maxCharacter ? '24px' : '0' }}
          />
        )}
        {type === 'textarea' ? (
          <STextArea
            value={value}
            onChange={(val) => {
              setValue(val);
            }}
            maxLength={maxCharacter}
          />
        ) : (
          <SInput
            value={value}
            onChange={(val) => {
              setValue(val);
            }}
            type="large"
            maxLength={maxCharacter}
          />
        )}
      </div>
    </div>
  );
};

type TranslationFieldMultilingual = {
  label: string;
  value?: Partial<MultilingualLabel>;
  name: string;
  type?: 'input' | 'textarea';
  maxCharacter?: number;
  extraValues?: Record<string, string | number | boolean | null | undefined>;
};

// the purpose of a hidden field is to pass values through this translation form to the api if needed
type TranslationFieldHidden = {
  label?: string;
  value?: Partial<MultilingualLabel> | number | string;
  name: string;
  type?: 'hidden';
  maxCharacter?: number;
  extraValues?: Record<string, string | number | boolean | null | undefined>;
};

export type TranslationField = TranslationFieldMultilingual | TranslationFieldHidden;

export type TranslationSection = {
  title?: string;
  items: TranslationField[];
};

export type STranslationInterfaceProps = {
  fields: TranslationSection[];
  changeOriginalLanguage: (language: Language) => void;
  originalLanguage?: Language;
  translatedLanguage: Language;
  availableLanguages: Language[];
  onFieldsChange: (fields: TranslationSection[]) => void;
  isFormDirty: boolean;
  onAutomaticTranslation: () => void;
};

type SetTranslatedValuesParams = {
  newValue: string;
  fieldIndex: number;
  fields: TranslationSection[];
  field: TranslationSection;
  item: TranslationField;
};

const isHiddenField = (item: TranslationField): item is TranslationFieldHidden => item.type === 'hidden';

export const STranslationInterface = ({
  fields,
  originalLanguage,
  translatedLanguage,
  availableLanguages,
  onFieldsChange,
  isFormDirty,
  onAutomaticTranslation,
  changeOriginalLanguage,
}: STranslationInterfaceProps) => {
  const [isAlreadyTranslated, setIsAlreadyTranslated] = useState(false);
  const [visibleWarningModal, displayWarningModal] = useState(false);
  const [calloutState, setCalloutState] = useState<keyof typeof calloutPropsMap>('default');
  const prevTranslatedLanguage = useRef(translatedLanguage.languageCode);

  // reset the calloutState to allow another translation when the translated language changes
  useEffect(() => {
    if (prevTranslatedLanguage.current !== translatedLanguage.languageCode) {
      setCalloutState('default');
    }
    prevTranslatedLanguage.current = translatedLanguage.languageCode;
  }, [translatedLanguage]);

  const isEmptyForm = useMemo(() => {
    const areEmptyFields = fields.map((field) =>
      field.items.reduce<boolean>((acc, item) => {
        if (isHiddenField(item) || !item.value?.id) return acc;
        return (
          acc && (!item.value?.[translatedLanguage.languageCode] || item.value[translatedLanguage.languageCode] === '')
        );
      }, true),
    );
    return areEmptyFields.every((isEmpty) => isEmpty);
  }, [fields, translatedLanguage]);

  const calloutPropsMap: Record<string, SCalloutProps> = {
    default: {
      type: 'accentuated',
      description: t('structural_component:s_translation_interface.description'),
      title: t('structural_component:s_translation_interface.title'),
      action: {
        label: t('structural_component:s_translation_interface.translate'),
        buttonType: 'button',
        leftIcon: 'google',
        onAction: () => {
          if (!isEmptyForm) {
            return displayWarningModal(true);
          }
          onTranslate();
        },
      },
    },
    retranslate: {
      type: 'accentuated',
      description: t('structural_component:s_translation_interface.description_restart'),
      title: t('structural_component:s_translation_interface.title'),
      action: {
        label: t('structural_component:s_translation_interface.translate_restart'),
        buttonType: 'button',
        leftIcon: 'google',
        onAction: () => {
          if (!isEmptyForm) {
            return displayWarningModal(true);
          }
          onTranslate();
        },
      },
    },
    success: {
      type: 'success',
      description: t('structural_component:s_translation_interface.description_success'),
      title: t('structural_component:s_translation_interface.title_success'),
    },
  };

  useEffect(() => {
    if (isFormDirty && isAlreadyTranslated) {
      setCalloutState('retranslate');
    }
  }, [isFormDirty]);

  const displayAutomaticTranslationSuccess = () => {
    setCalloutState('success');
  };

  if (!originalLanguage || !translatedLanguage) {
    return null;
  }

  const setTranslatedValues = useCallback(
    ({ newValue, fieldIndex, fields, item, field }: SetTranslatedValuesParams) =>
      onFieldsChange([
        ...fields.slice(0, fieldIndex),
        {
          ...field,
          items: field.items.map((fieldItem) =>
            fieldItem.name === item.name && !isHiddenField(fieldItem)
              ? {
                  ...fieldItem,
                  value: {
                    ...fieldItem.value,
                    [translatedLanguage.languageCode]: newValue,
                  },
                }
              : fieldItem,
          ),
        },
        ...fields.slice(fieldIndex + 1),
      ]),
    [],
  );

  const getLanguageOptions = useCallback((): OptionType[] => {
    return availableLanguages
      .filter((lang) => lang.languageCode !== originalLanguage.languageCode)
      .map((lang) => ({
        type: 'language' as const,
        key: lang.languageCode,
        props: {
          countryCode: lang.countryCode,
          text: lang.languageName,
          onClick: () => changeOriginalLanguage(lang),
        },
      }));
  }, [availableLanguages, originalLanguage, changeOriginalLanguage]);

  const onTranslate = useCallback(async () => {
    const itemsId = fields.map((field) =>
      field.items.reduce<number[]>(
        (acc, item) =>
          !isHiddenField(item) && item.value?.id && item.value?.[originalLanguage.languageCode]
            ? [...acc, item.value.id]
            : acc,
        [],
      ),
    );

    try {
      const res = await fetchTranslations({
        sourceLanguageCode: originalLanguage.languageCode,
        targetLanguageCode: translatedLanguage.languageCode,
        multilingualLabelIds: itemsId.flat(),
      });

      const formattedRes = res.multilingualLabels.reduce<Record<number, MultilingualLabel>>((acc, item) => {
        return {
          ...acc,
          [item.id]: item,
        };
      }, {});

      const newTranslatedFields = fields.map((field) => ({
        ...field,
        items: field.items.map((item) => {
          if (!isHiddenField(item) && item.value?.id && formattedRes[item.value.id]) {
            return {
              ...item,
              value: formattedRes[item.value.id],
            };
          }
          return item;
        }),
      }));

      onFieldsChange(newTranslatedFields);
      displayAutomaticTranslationSuccess();
      onAutomaticTranslation();
      setIsAlreadyTranslated(true);
    } catch (error) {
      enqueueBasicAlert(
        {
          id: 'translation_failed',
          status: 'error',
          title: t('structural_component:s_translation_interface.error'),
        },
        ALERT_DURATION,
      );
    }
  }, [fields, onFieldsChange, originalLanguage, translatedLanguage]);

  return (
    <div style={styles.wrapper}>
      <div style={styles.title}>
        <div style={styles.language}>
          {availableLanguages.length > 1 ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ ...TYPOGRAPHY.H1_BOLD, marginRight: 8 }}>{originalLanguage.languageName}</div>
              <ULanguageMenu countryCode={originalLanguage.countryCode} options={getLanguageOptions()} />
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={TYPOGRAPHY.H1_BOLD}>{originalLanguage.languageName}</div>
              <UFlag countryCode={originalLanguage.countryCode} size={16} style={{ marginLeft: 8 }} />
            </div>
          )}
        </div>
        <UIcon name="arrow-right" size={16} />
        <div style={styles.language}>
          <div style={TYPOGRAPHY.H1_BOLD}>{translatedLanguage.languageName}</div>
          <UFlag countryCode={translatedLanguage.countryCode} size={16} style={{ marginLeft: 8 }} />
        </div>
      </div>

      <SCallout
        style={styles.translateGoogleSection}
        type={calloutPropsMap[calloutState].type}
        title={calloutPropsMap[calloutState].title}
        description={calloutPropsMap[calloutState].description}
        action={calloutPropsMap[calloutState]?.action}
      />

      {fields.map((field, fieldIndex) => (
        <div key={`${field.title}-${fieldIndex}`} style={styles.section}>
          <h2>{field.title}</h2>

          {field.items.map((item) => {
            if (isHiddenField(item) || !item.value?.[originalLanguage.languageCode]) return null;

            return (
              <TranslateField
                key={item.label}
                label={item.label}
                value={item?.value?.[translatedLanguage.languageCode] || ''}
                originalValue={item?.value?.[originalLanguage.languageCode] || ''}
                maxCharacter={item.maxCharacter}
                type={item.type}
                setValue={(newValue) => {
                  setTranslatedValues({
                    newValue,
                    fieldIndex,
                    fields,
                    field,
                    item,
                  });
                }}
              />
            );
          })}
        </div>
      ))}

      <MModal
        onCloseModal={() => displayWarningModal(false)}
        visible={visibleWarningModal}
        type="dialog"
        onSecondAction={() => displayWarningModal(false)}
        showExitCross
        actionButtonType="destructive"
        title={
          isFormDirty && isAlreadyTranslated
            ? t('structural_component:s_translation_interface.warning_automatic_translation_title_restart')
            : t('structural_component:s_translation_interface.warning_automatic_translation_title')
        }
        description={t('structural_component:s_translation_interface.warning_automatic_translation_description')}
        labelActionButton={
          isFormDirty && isAlreadyTranslated
            ? t('structural_component:s_translation_interface.warning_automatic_translation_action_button_restart')
            : t('structural_component:s_translation_interface.warning_automatic_translation_action_button')
        }
        labelSecondButton={t(
          'structural_component:s_translation_interface.warning_automatic_translation_second_button',
        )}
        onAction={() => {
          displayWarningModal(false);
          onTranslate();
        }}
      />
    </div>
  );
};
